import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Stammdaten } from "../shared/types.js";
import { api } from "./api.js";

/**
 * Stammdaten einmal laden und teilen.
 *
 * Vorher holte jede Seite sie im eigenen useEffect. Da App.tsx Seiten beim
 * Reiterwechsel aushängt, wurde /api/stammdaten bei jedem Wechsel erneut
 * geholt — und mit Angebotsmaske plus Schnellkalkulation wären es drei
 * unabhängige Kopien geworden, die auseinanderlaufen können.
 *
 * `neuLaden` ist nötig, weil die Stammdatenpflege über /verwaltung schreibt,
 * also einen anderen Endpunkt: ohne das wäre der Bestand nach einer
 * Preisänderung veraltet.
 */
interface StammdatenKontext {
  stamm: Stammdaten | null;
  fehler: string | null;
  /** Wann geladen — die Schnellkalkulation zeigt den Preisstand an. */
  geladenAm: number | null;
  neuLaden: () => void;
}

const Kontext = createContext<StammdatenKontext>({
  stamm: null,
  fehler: null,
  geladenAm: null,
  neuLaden: () => {},
});

export const useStammdaten = () => useContext(Kontext);

export function StammdatenProvider({ children }: { children: React.ReactNode }) {
  const [stamm, setStamm] = useState<Stammdaten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [geladenAm, setGeladenAm] = useState<number | null>(null);

  const neuLaden = useCallback(() => {
    api
      .get<Stammdaten>("/stammdaten")
      .then((d) => {
        setStamm(d);
        setGeladenAm(Date.now());
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof Error ? e.message : "Stammdaten nicht ladbar."));
  }, []);

  useEffect(neuLaden, [neuLaden]);

  // Memoisieren, sonst rechnet jede abhängige useMemo bei jedem Render neu.
  const wert = useMemo(
    () => ({ stamm, fehler, geladenAm, neuLaden }),
    [stamm, fehler, geladenAm, neuLaden],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}
